import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  DEFAULT_TENANT_ID,
  chunkCouponIds,
  type AppUser,
  type Coupon,
  type CouponUsage,
  type LotteryEvent,
  type LotteryStockDoc,
  type LotteryUserDaily,
} from '@kawai/shared';
import { db } from '../firebase.js';

/**
 * M-164: イベント抽選（ルーレット）の閲覧用 repository。読取のみ（rules も write:false）。
 *
 * lottery_events / lottery_stock は doc id が `{tenantId}__…` 先頭で rules が path 判定するため
 * doc 未作成でも permission-denied にならない。lottery_user_daily は `belongsToMyTenant()` 判定
 * だが list クエリに `where tenantId ==` を付けるので同じく安全。
 */

const LOTTERY_EVENTS_COL = 'lottery_events';
const LOTTERY_STOCK_COL = 'lottery_stock';
const LOTTERY_USER_DAILY_COL = 'lottery_user_daily';
const USERS_COL = 'users';

/**
 * テナントの抽選イベント設定を全件取得する（通常 1〜数件 / 「最新 1 件」の選別は純関数
 * `pickLatestLotteryEvent` に任せる = orderBy を付けないので複合 index が要らない）。
 */
export async function fetchLotteryEvents(tenantId: string): Promise<LotteryEvent[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const snap = await getDocs(
    query(collection(db, LOTTERY_EVENTS_COL), where('tenantId', '==', tid)),
  );
  return snap.docs.map((d) => d.data() as LotteryEvent);
}

/** イベントのカウンタ doc を全部読む（等値条件のみ / index 不要）。 */
export async function fetchLotteryStock(
  tenantId: string,
  eventId: string,
): Promise<LotteryStockDoc[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const snap = await getDocs(
    query(
      collection(db, LOTTERY_STOCK_COL),
      where('tenantId', '==', tid),
      where('eventId', '==', eventId),
    ),
  );
  return snap.docs.map((d) => d.data() as LotteryStockDoc);
}

/**
 * 回数の多い利用者（1 人 1 日 doc を count 降順）。
 * 複合 index `(tenantId ASC, eventId ASC, count DESC)` を使用（firestore.indexes.json 追加済）。
 */
export async function fetchLotteryTopUsers(
  tenantId: string,
  eventId: string,
  max = 10,
): Promise<LotteryUserDaily[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const snap = await getDocs(
    query(
      collection(db, LOTTERY_USER_DAILY_COL),
      where('tenantId', '==', tid),
      where('eventId', '==', eventId),
      orderBy('count', 'desc'),
      fbLimit(max),
    ),
  );
  return snap.docs.map((d) => d.data() as LotteryUserDaily);
}

/**
 * users/line:<lineUserId> の displayName を best-effort で引く。
 * doc が無い・rules で拒否された・通信失敗はすべて null（呼び出し側が末尾 6 桁に落とす）。
 * 上位 10 人分なので並列 getDoc で足りる（users 一覧全件を読まない）。
 */
export async function fetchLineDisplayNames(
  lineUserIds: readonly string[],
): Promise<Record<string, string | null>> {
  const unique = Array.from(new Set(lineUserIds));
  const entries = await Promise.all(
    unique.map(async (id): Promise<[string, string | null]> => {
      try {
        const snap = await getDoc(doc(db, USERS_COL, `line:${id}`));
        if (!snap.exists()) return [id, null];
        const name = (snap.data() as Partial<AppUser>).displayName;
        return [id, typeof name === 'string' && name.length > 0 ? name : null];
      } catch {
        // 表示名は飾りなので失敗を画面エラーにしない（抽選状況の本体は別クエリ）。
        return [id, null];
      }
    }),
  );
  return Object.fromEntries(entries);
}

// ---------- M-164 第 2 弾: 当選クーポンページ ----------

const COUPONS_COL = 'coupons';
const COUPON_USAGES_COL = 'coupon_usages';

/**
 * 当選クーポンの家族代表（各家族の #01）を id 直読みで引く。無ければ null（「未作成」表示）。
 * 件数は家族数（全員分 1 + 景品数）なので並列 getDoc で足りる。
 */
export async function fetchCouponsByIds(
  couponIds: readonly string[],
): Promise<Record<string, Coupon | null>> {
  const unique = Array.from(new Set(couponIds));
  const entries = await Promise.all(
    unique.map(async (id): Promise<[string, Coupon | null]> => {
      const snap = await getDoc(doc(db, COUPONS_COL, id));
      return [
        id,
        snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Coupon, 'id'>) } : null,
      ];
    }),
  );
  return Object.fromEntries(entries);
}

/**
 * 抽選で付与した個人票（`grantSource == 'lottery'`）を全部読む（等値条件のみ / index 不要）。
 * 家族ごとの配布済・使用済は純関数 `summarizeLotteryCoupons` が数える。
 */
export async function fetchLotteryCouponUsages(tenantId: string): Promise<CouponUsage[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const snap = await getDocs(
    query(
      collection(db, COUPON_USAGES_COL),
      where('tenantId', '==', tid),
      where('grantSource', '==', 'lottery'),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CouponUsage, 'id'>) }));
}

/**
 * M-166: 指定クーポン（複製 #01〜#10 の OR）の coupon_usages を読む（セグメント条件の判定材料）。
 * `in` 句は 10 件ずつ（`chunkCouponIds`）。grantSource は絞らない — 同じクーポンを別経路で
 * 取得した人も「取得した人」なので、functions 側（`loadSegmentEvalContext`）と同じ条件にする。
 */
export async function fetchCouponUsagesByCouponIds(
  tenantId: string,
  couponIds: ReadonlyArray<string>,
): Promise<CouponUsage[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const out: CouponUsage[] = [];
  for (const chunk of chunkCouponIds(couponIds)) {
    const snap = await getDocs(
      query(
        collection(db, COUPON_USAGES_COL),
        where('tenantId', '==', tid),
        where('couponId', 'in', chunk),
      ),
    );
    for (const d of snap.docs) {
      out.push({ id: d.id, ...(d.data() as Omit<CouponUsage, 'id'>) });
    }
  }
  return out;
}
